import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { CSSProperties } from 'react';

import { GnbRedDotTypes } from '@flipster/apis/v1/red-dot/types';
import { getTradingCompetitionsListApi } from '@flipster/apis/v1/tradingCompetition';
import { TradingCompetition } from '@flipster/apis/v1/tradingCompetition/types';
import { TradingLeaderboardMyRankingItem } from '@flipster/apis/v2/trading-leaderboards/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { defaultTradFiSymbol, getTradFiCategorySymbols } from '@flipster/features/src/shared/rwa';
import { symbolsQueryAtom } from '@flipster/features/src/shared/symbol/symbols';
import { tradingLeaderboardMyRankingBunja } from '@flipster/features/src/trading-leaderboard-page/bunja';
import { userPoolEligibilityBunja } from '@flipster/features/src/userpool/user-pool.state';
import { i18nBunja } from '@flipster/i18n';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { CookieKeys, getCookieBunja } from '@flipster/utils/cookie';

export interface Nav {
  isHide?: boolean;
  redDotKey?: GnbRedDotTypes;
}

export interface LeaderboardNav extends Nav {
  type: 'leaderboard';
  label: string;
  url: string;
  ranking: TradingLeaderboardMyRankingItem | undefined;
  isHideMobile?: boolean;
}

export interface LinkNav extends Nav {
  type: 'link';
  label: string;
  url: string;
  newTab?: boolean;
  isHideMobile?: boolean;
  animatedGradient?: boolean;
}

export interface MenuNav extends Nav {
  type: 'menu';
  label: string; // React node
  children: (LinkNav | LeaderboardNav)[];
  style?: CSSProperties;
}

export interface GroupNav extends Nav {
  type: 'group';
  label: string;
  children: MenuNav[];
}

export type Navigations = LinkNav | MenuNav | GroupNav;

export const separateGroup = (navigations: (MenuNav | LinkNav | GroupNav)[]) => {
  return navigations.flatMap((nav) => {
    if (nav.type === 'group') return nav.children;
    return nav;
  });
};

const navBaseBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom: rawIsLoggedInAtom } = bunja.use(authBunja);
  const { isSpotOnlyRegulationAtom: rawIsSpotOnlyRegulationAtom } = bunja.use(userRegulationBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();

  // 서버 렌더와 첫 클라이언트 렌더에서는 항상 false로 두고, 마운트 이후에만 실제 인증/규제 상태를 반영한다.
  // GNB 항목이 로그인/규제 상태에 따라 태그 종류(<a> ↔ <div>)나 노드 유무(isHide)까지 바뀌면서
  // 발생하던 hydration mismatch를 막기 위함이다.
  const isHydratedAtom = atom(false);
  isHydratedAtom.debugLabel = 'nav - is hydrated';
  bunja.effect(() => {
    store.set(isHydratedAtom, true);
  });

  const isLoggedInAtom = atom((get) => get(isHydratedAtom) && get(rawIsLoggedInAtom));
  isLoggedInAtom.debugLabel = 'nav - hydrated is logged in';

  const isSpotOnlyRegulationAtom = atom((get) => get(isHydratedAtom) && get(rawIsSpotOnlyRegulationAtom));
  isSpotOnlyRegulationAtom.debugLabel = 'nav - hydrated is spot only';

  return { t, isLoggedInAtom, isSpotOnlyRegulationAtom };
});
navBaseBunja.debugLabel = 'navBaseBunja';

export const navBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);

  const { tradeNavAtom } = bunja.use(tradeNavBunja);
  const { tradFiNavAtom } = bunja.use(tradFiNavBunja);
  const { copyTradingNavAtom } = bunja.use(copyTradingNavBunja);
  const { earnNavAtom } = bunja.use(earnNavBunja);
  const { historyNavAtom } = bunja.use(historyNavBunja);
  const { moreNavAtom } = bunja.use(moreNavBunja);

  const navAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const tradeNav = get(tradeNavAtom);
    const tradFiNav = get(tradFiNavAtom);
    const copyTradingNav = get(copyTradingNavAtom);
    const earnNav = get(earnNavAtom);
    const historyNav = get(historyNavAtom);
    const moreNav = get(moreNavAtom);
    return [
      ...tradeNav,
      tradFiNav,
      { type: 'link', label: t('Navigation.r250401.Market'), url: `/market/${isSpotOnly ? 'spot' : 'perpetual'}` },
      copyTradingNav,
      ...earnNav,
      { type: 'link', label: t('Navigation.r250401.Assets'), url: '/assets', isHide: !isLoggedIn },
      ...historyNav,
      ...moreNav,
    ];
  });

  return { navAtom };
});
navBunja.debugLabel = 'navBunja';

const tradeNavBunja = bunja(() => {
  const { isVaraRegulatedAtom } = bunja.use(userRegulationBunja);
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);
  const { cookieAtom: perpReferralSymbolAtom } = bunja.use(getCookieBunja(CookieKeys.ReferrerSymbol));
  const { cookieAtom: spotReferralSymbolAtom } = bunja.use(getCookieBunja(CookieKeys.SpotReferrerSymbol));

  const tradeNavAtom = atom<Navigations[]>((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const perpTradeUrl = get(perpTradeUrlAtom);
    const spotTradeUrl = get(spotTradeUrlAtom);
    return [
      {
        type: 'menu',
        label: t('Navigation.r250401.Trade'),
        isHide: isSpotOnly,
        children: [
          { type: 'link', label: t('Navigation.r250401.Perpetual'), url: perpTradeUrl },
          { type: 'link', label: t('Navigation.r250401.Spot'), url: spotTradeUrl },
        ],
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Trade'),
        url: spotTradeUrl,
        isHide: !isSpotOnly,
      },
    ];
  });

  const perpTradeUrlAtom = atom<string>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const perpReferralSymbol = get(perpReferralSymbolAtom);
    return isLoggedIn && perpReferralSymbol
      ? `/trade/perpetual/${perpReferralSymbol}`
      : '/trade/perpetual/BTCUSDT.PERP';
  });

  const spotTradeUrlAtom = atom<string>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const spotReferralSymbol = get(spotReferralSymbolAtom);
    const isVaraRegulated = get(isVaraRegulatedAtom);
    if (isVaraRegulated) {
      return isLoggedIn && spotReferralSymbol && spotReferralSymbol?.startsWith('VARA_')
        ? `/trade/spot/${spotReferralSymbol}`
        : '/trade/spot/VARA_BTCUSDT';
    }
    return isLoggedIn && spotReferralSymbol && !spotReferralSymbol?.startsWith('VARA_')
      ? `/trade/spot/${spotReferralSymbol}`
      : '/trade/spot/BTCUSDT';
  });

  return { tradeNavAtom };
});
tradeNavBunja.debugLabel = 'tradeNavBunja';

const tradFiNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);
  const { cookieAtom: tradFiReferrerSymbolAtom } = bunja.use(getCookieBunja(CookieKeys.TradFiReferrerSymbol));

  const tradFiTradeUrlAtom = atom<string>((get) => {
    const lastTradFiSymbol = get(tradFiReferrerSymbolAtom);
    if (get(isLoggedInAtom) && lastTradFiSymbol) return `/trade/perpetual/${lastTradFiSymbol}`;
    // 쿠키가 없을 때만 symbols 쿼리를 구독해 첫 방문 기본값(거래량 1위 TradFi)을 계산한다.
    const topTradFiSymbol = getTradFiCategorySymbols(get(symbolsQueryAtom).data, 'all')[0];
    return `/trade/perpetual/${topTradFiSymbol ?? defaultTradFiSymbol}`;
  });

  const tradFiNavAtom = atom<Navigations>((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    return {
      type: 'link',
      label: t('Navigation.r260625.Tradfi'),
      url: get(tradFiTradeUrlAtom),
      isHide: isSpotOnly,
      animatedGradient: true,
    };
  });

  return { tradFiNavAtom };
});
tradFiNavBunja.debugLabel = 'tradFiNavBunja';

const copyTradingNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);

  const copyTradingNavAtom = atom<Navigations>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    if (!isLoggedIn) {
      return {
        type: 'link',
        label: t('Navigation.r251107.Copy'),
        isHide: isSpotOnly,
        url: '/copy-trading',
      };
    }
    return {
      type: 'menu',
      label: t('Navigation.r251107.Copy'),
      isHide: isSpotOnly,
      children: [
        { type: 'link', label: t('Common.r250925.Discover'), url: '/copy-trading' },
        {
          type: 'link',
          label: t('Copy.r251107.Copy_activity'),
          url: '/copy-trading/activity',
          redDotKey: 'COPY_TRADING_COPY_ACTIVITY',
          isHideMobile: true,
        },
      ],
    };
  });

  return { copyTradingNavAtom };
});
copyTradingNavBunja.debugLabel = 'copyTradingNavBunja';

const earnNavBunja = bunja(() => {
  const { t, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);
  const { userPoolEligibilityAtom } = bunja.use(userPoolEligibilityBunja);

  const earnNavAtom = atom<Navigations[]>((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const userPoolEligibility = get(userPoolEligibilityAtom);

    return [
      {
        type: 'menu',
        label: t('Navigation.r250401.Earn_plus'),
        isHide: isSpotOnly,
        children: [
          {
            type: 'link',
            label: t('Autogen_common.r251126.s_Dynamic_Earn'),
            url: '/dynamic-earn',
          },
          {
            type: 'link',
            label: t('Autogen_common.r251126.s_Flipster_Earn'),
            url: '/earn',
          },
          {
            type: 'link',
            label: t('Navigation.r250829.New_joiner_pool'),
            url: `/userpool/${userPoolEligibility?.id}`,
            isHide: !userPoolEligibility?.eligible,
          },
          { type: 'link', label: t('Navigation.r250401.Launchpool'), url: '/launchpool' },
        ],
      },
    ];
  });

  return { earnNavAtom };
});
earnNavBunja.debugLabel = 'earnNavBunja';

const historyNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);

  const historyNavAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    return [
      {
        type: 'group',
        label: t('Navigation.r250401.History'),
        isHide: !isLoggedIn,
        children: [
          {
            type: 'menu',
            label: t('Navigation.r250401.Perpetual_history'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: !isLoggedIn || isSpotOnly,
            children: [
              { type: 'link', label: t('Navigation.r250401.Position'), url: '/history/perpetual/position' },
              { type: 'link', label: t('Navigation.r250401.Pnl'), url: '/history/perpetual/pnl' },
              { type: 'link', label: t('Navigation.r250401.Funding'), url: '/history/perpetual/funding' },
              { type: 'link', label: t('Navigation.r250401.Trade'), url: '/history/perpetual/trade' },
              { type: 'link', label: t('Navigation.r250401.Profit_boost'), url: '/history/perpetual/profit-boost' },
              { type: 'link', label: t('Navigation.r250401.Airdrop'), url: '/history/perpetual/airdrop' },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Spot_history'),
            style: { gridColumn: 1, gridRow: isSpotOnly ? 1 : 2 },
            isHide: !isLoggedIn,
            children: [
              { type: 'link', label: t('Navigation.r250401.Pnl'), url: '/history/spot/pnl' },
              { type: 'link', label: t('Navigation.r250401.Trade'), url: '/history/spot/trade' },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Transaction_history'),
            style: { gridColumn: 2, gridRow: '1/3' },
            isHide: !isLoggedIn,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Earn'),
                url: '/history/transaction/earn',
                isHide: isSpotOnly,
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Launchpool_reward'),
                url: '/history/transaction/launchpool-reward',
                isHide: isSpotOnly,
              },
              { type: 'link', label: t('Navigation.r250401.Deposit'), url: '/history/transaction/deposit' },
              { type: 'link', label: t('Navigation.r250401.Withdrawal'), url: '/history/transaction/withdrawal' },
              { type: 'link', label: t('Navigation.r250401.Conversion'), url: '/history/transaction/conversion' },
              {
                type: 'link',
                label: t('Navigation.r251107.Copy_trading'),
                url: '/history/transaction/copy-trading',
                isHide: isSpotOnly,
              },
              {
                type: 'link',
                label: t('Navigation.r250818.Referral_affiliate'),
                url: '/history/transaction/referral-and-affiliate',
                isHide: isSpotOnly,
              },
            ],
          },
        ],
      },
    ];
  });

  return { historyNavAtom };
});
historyNavBunja.debugLabel = 'historyNavBunja';

const moreNavBunja = bunja(() => {
  const { t, isLoggedInAtom, isSpotOnlyRegulationAtom } = bunja.use(navBaseBunja);
  const { myOptInIntervalRankingsAtom } = bunja.use(tradingLeaderboardMyRankingBunja);

  const competitionsAtom = atom<TradingCompetition[]>((get) => get(competitionsQueryAtom).data || []);
  const competitionsQueryAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: ['getTradingCompetitionsListApi', 'GNB'],
      queryFn: () => getTradingCompetitionsListApi(),
      select: (res: AxiosResponse<{ competitions: TradingCompetition[] }, unknown, {}>) =>
        res.data.competitions
          .filter(
            (c) => (c.competitionStatus === 'UPCOMING' || c.competitionStatus === 'ONGOING') && !!c.participationTime,
          )
          .sort((a, b) => new Date(a.competitionStartTime).getTime() - new Date(b.competitionStartTime).getTime()),
      enabled: isLoggedIn,
    };
  });

  const moreNavAtom = atom<Navigations[]>((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const competitions = get(competitionsAtom);
    const myTradingLeaderboardsRanking = get(myOptInIntervalRankingsAtom);
    return [
      {
        type: 'group',
        label: t('Navigation.r250401.More'),
        children: [
          {
            type: 'menu',
            label: t('Navigation.r251104.Programs'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: isSpotOnly,
            children: (
              [
                {
                  type: 'link',
                  label: t('Navigation.r250401.Referral_program'),
                  url: '/marketing/referral',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Vip_program'),
                  url: '/vip-program',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Affiliate_program'),
                  url: '/marketing/affiliates',
                  newTab: true,
                },
              ] satisfies (LeaderboardNav | LinkNav | false)[]
            ).filter(Boolean),
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Challenges'),
            style: { gridColumn: 1, gridRow: 2 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Sparks_auction'),
                url: '/spark/auction',
              },
              ...(isLoggedIn
                ? competitions.map<LinkNav>((c) => ({
                    type: 'link',
                    label: c.title,
                    url: `/competitions/${c.competitionUrl}`,
                  }))
                : []),
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Pnl_leaderboard'),
            style: { gridColumn: 2, gridRow: 1 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Weekly'),
                url: `/trading-leaderboard?interval=WEEKLY&tier=${myTradingLeaderboardsRanking?.WEEKLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.WEEKLY,
              },
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Monthly'),
                url: `/trading-leaderboard?interval=MONTHLY&tier=${myTradingLeaderboardsRanking?.MONTHLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.MONTHLY,
              },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Learn'),
            style: isSpotOnly ? { gridColumn: 1, gridRow: 1 } : { gridColumn: 2, gridRow: 2 },
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Blog'),
                url: '/blog',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Glossary'),
                url: '/glossary',
              },
            ],
          },
        ],
      },
    ];
  });

  return { moreNavAtom };
});
moreNavBunja.debugLabel = 'moreNavBunja';
